import { useEffect, useMemo, useState } from 'react';

import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	List,
	ListItemButton,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	TextField,
} from '@mui/material';

import { OperationMetadataModel } from '../../../../api/api';
import useApiContexts from '../../Hooks/useApiContexts';
import useApiOperationsOfContext from '../../Hooks/useApiOperationsOfContext';
import { OperationRefModel } from '../../Models/DomainServiceModel';

type OperationPickerDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	operationRefs: OperationRefModel[] | undefined;
	onOperationRefsChange: (operationRefs: OperationRefModel[] | undefined) => void;
	multiple?: boolean;
};

function containsOperation(operations: OperationRefModel[], operationId: string) {
	return operations.some((operation) => operation.Id === operationId);
}

export default function OperationPickerDialog({ open, setOpen, operationRefs, onOperationRefsChange, multiple = false }: OperationPickerDialogProps) {
	const [selectedContextId, setSelectedContextId] = useState<string>('');
	const [selectedOperations, setSelectedOperations] = useState<OperationRefModel[]>([]);
	const [filter, setFilter] = useState('');
	const { data: contexts = [] } = useApiContexts();
	const { data: contextOperations = [] } = useApiOperationsOfContext(selectedContextId || undefined);

	useEffect(() => {
		if (open) {
			setSelectedOperations(operationRefs ?? []);
			setSelectedContextId(operationRefs?.[0]?.ContextId || contexts[0]?.id || '');
			setFilter('');
		}
	}, [contexts, open, operationRefs]);

	const visibleOperations = useMemo(() => {
		const normalizedFilter = filter.trim().toLocaleLowerCase();
		if (!normalizedFilter) {
			return contextOperations;
		}

		return contextOperations.filter((operation) => operation.name.toLocaleLowerCase().includes(normalizedFilter));
	}, [contextOperations, filter]);

	const toggleOperation = (operation: OperationMetadataModel) => {
		setSelectedOperations((current) => {
			if (containsOperation(current, operation.id)) {
				return current.filter((candidate) => candidate.Id !== operation.id);
			}

			const context = contexts.find((candidate) => candidate.id === operation.contextId);
			const selected = { Id: operation.id, Name: operation.name, ContextId: operation.contextId, ContextName: context?.name };
			return multiple ? [...current, selected] : [selected];
		});
	};

	const handleSave = () => {
		onOperationRefsChange(selectedOperations.length > 0 ? selectedOperations : undefined);
		setOpen(false);
	};

	return (
		<Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
			<DialogTitle>{multiple ? 'Allowed Operations' : 'Select Operation'}</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={2} sx={{ height: '60vh' }}>
					<FormControl fullWidth>
						<InputLabel>Context</InputLabel>
						<Select value={selectedContextId} label="Context" onChange={(event) => setSelectedContextId(event.target.value)}>
							{contexts.map((context) => (
								<MenuItem key={context.id} value={context.id}>
									{context.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField label="Search operations" value={filter} onChange={(event) => setFilter(event.target.value)} fullWidth />
					<List dense sx={{ flexGrow: 1, overflowY: 'auto' }}>
						{visibleOperations.map((operation) => (
							<ListItemButton key={operation.id} onClick={() => toggleOperation(operation)}>
								{multiple && <Checkbox checked={containsOperation(selectedOperations, operation.id)} tabIndex={-1} disableRipple />}
								<ListItemText primary={operation.name} secondary={operation.description} />
							</ListItemButton>
						))}
					</List>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>Cancel</Button>
				<Button onClick={handleSave}>Save</Button>
			</DialogActions>
		</Dialog>
	);
}
